import { useEffect, useState } from "react";
import { resolveValidStoreId } from "@medicorepay/shared";
import { useAuthStore } from "../../stores/authStore";
import { showToast } from "../../lib/toast";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { productService, Batch } from "../../services/product.service";
import {
  stockAdjustmentService,
  StockAdjustment,
  CreateStockAdjustmentDto,
  AdjustmentType,
} from "../../services/stock-adjustment.service";

const TYPE_OPTIONS: AdjustmentType[] = [
  "OPENING_STOCK",
  "TRANSFER_IN",
  "TRANSFER_OUT",
  "CORRECTION",
  "DAMAGE",
  "EXPIRY",
  "THEFT",
  "RETURN_TO_SUPPLIER",
];

const WRITE_OFF_TYPES: AdjustmentType[] = ["DAMAGE", "EXPIRY"];

const TYPE_INFO: Record<
  string,
  { label: string; description: string; color: string; direction: string }
> = {
  OPENING_STOCK: {
    label: "Opening Stock",
    description: "Set initial stock when starting inventory",
    color: "bg-blue-100 text-blue-800",
    direction: "IN",
  },
  TRANSFER_IN: {
    label: "Transfer In",
    description: "Stock received from another store/location",
    color: "bg-green-100 text-green-800",
    direction: "IN",
  },
  TRANSFER_OUT: {
    label: "Transfer Out",
    description: "Stock sent to another store/location",
    color: "bg-yellow-100 text-yellow-800",
    direction: "OUT",
  },
  CORRECTION: {
    label: "Correction",
    description: "Fix stock count after physical verification",
    color: "bg-purple-100 text-purple-800",
    direction: "BOTH",
  },
  DAMAGE: {
    label: "Damage",
    description: "Stock damaged and cannot be sold",
    color: "bg-red-100 text-red-800",
    direction: "OUT",
  },
  EXPIRY: {
    label: "Expiry",
    description: "Stock expired and needs to be removed",
    color: "bg-orange-100 text-orange-800",
    direction: "OUT",
  },
  THEFT: {
    label: "Theft/Loss",
    description: "Stock lost due to theft or unexplained shortage",
    color: "bg-red-100 text-red-800",
    direction: "OUT",
  },
  RETURN_TO_SUPPLIER: {
    label: "Return to Supplier",
    description: "Stock returned to supplier/distributor",
    color: "bg-gray-100 text-gray-800",
    direction: "OUT",
  },
};

export function StockAdjustmentsPage() {
  const selectedStoreId = useAuthStore((state) =>
    resolveValidStoreId(
      state.selectedStoreId,
      state.user?.storeId,
      state.user?.stores,
    ),
  );
  const [adjustments, setAdjustments] = useState<StockAdjustment[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [directionFilter, setDirectionFilter] = useState<string>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [batchSearch, setBatchSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<"adjustment" | "writeoff">("adjustment");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    payload: null as CreateStockAdjustmentDto | null,
  });
  const limit = 20;

  const [formData, setFormData] = useState<CreateStockAdjustmentDto>({
    storeId: selectedStoreId || "",
    batchId: "",
    type: "CORRECTION",
    quantity: 1,
    reason: "",
    notes: "",
    reference: "",
    direction: "OUT",
  });

  useEffect(() => {
    loadBatches();
  }, [selectedStoreId]);

  useEffect(() => {
    loadAdjustments();
  }, [
    page,
    typeFilter,
    directionFilter,
    search,
    selectedStoreId,
    refreshVersion,
  ]);

  const loadBatches = async () => {
    if (!selectedStoreId) {
      setBatches([]);
      return;
    }
    try {
      const data = await productService.getBatches({
        storeId: selectedStoreId,
        inStock: true,
      });
      setBatches(data || []);
    } catch (err) {
      setBatches([]);
    }
  };

  const loadAdjustments = async () => {
    if (!selectedStoreId) {
      setAdjustments([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await stockAdjustmentService.getAll({
        page,
        limit,
        storeId: selectedStoreId,
        type: typeFilter !== "all" ? (typeFilter as any) : undefined,
        direction:
          directionFilter !== "all"
            ? (directionFilter as "IN" | "OUT")
            : undefined,
        search: search || undefined,
      });
      setAdjustments(response.data || []);
      setTotal(response.meta?.total || 0);
    } catch (err: any) {
      const apiErrors: string[] = err.response?.data?.errors || [];
      const message = err.response?.data?.message;
      showToast.error(
        apiErrors.length > 0
          ? apiErrors.join(" ")
          : message || "Failed to load stock adjustments",
      );
      setAdjustments([]);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (modeType: "adjustment" | "writeoff") => {
    setMode(modeType);
    setFormData({
      storeId: selectedStoreId || "",
      batchId: "",
      type: modeType === "writeoff" ? "DAMAGE" : "CORRECTION",
      quantity: 1,
      reason: "",
      notes: "",
      reference: "",
      direction: modeType === "writeoff" ? "OUT" : "OUT",
    });
    setBatchSearch("");
    setIdempotencyKey(crypto.randomUUID());
    setIsModalOpen(true);
  };

  const handleSubmit = () => {
    if (!selectedStoreId) {
      showToast.error("Select a store first");
      return;
    }
    if (!formData.storeId) {
      showToast.error("Store is required");
      return;
    }
    if (!formData.batchId) {
      showToast.error("Batch is required");
      return;
    }
    if (!formData.quantity || formData.quantity <= 0) {
      showToast.error("Quantity must be greater than 0");
      return;
    }
    if ((formData.reason || "").trim().length < 3) {
      showToast.error("A meaningful reason is required");
      return;
    }
    if (
      (formData.type === "TRANSFER_IN" || formData.type === "TRANSFER_OUT") &&
      (formData.reference || "").trim().length < 2
    ) {
      showToast.error("Enter a transfer reference");
      return;
    }

    const selectedBatch = batches.find(
      (batch) => batch.id === formData.batchId,
    );
    const direction =
      TYPE_INFO[formData.type]?.direction === "BOTH"
        ? formData.direction
        : TYPE_INFO[formData.type]?.direction;
    if (
      direction === "OUT" &&
      selectedBatch &&
      formData.quantity > Number(selectedBatch.quantity)
    ) {
      showToast.error(
        `Only ${selectedBatch.quantity} units are available in this batch`,
      );
      return;
    }

    setConfirmModal({
      isOpen: true,
      payload: {
        ...formData,
        reason: formData.reason.trim(),
        notes: formData.notes?.trim() || undefined,
        reference: formData.reference?.trim() || undefined,
      },
    });
  };

  const handleConfirm = async () => {
    if (!confirmModal.payload) return;

    try {
      setIsSubmitting(true);
      const result = await stockAdjustmentService.create(
        confirmModal.payload,
        idempotencyKey,
      );
      const available = result.batch?.quantity;
      showToast.success(
        available === null || available === undefined
          ? "Stock adjustment recorded"
          : `Stock adjustment recorded. Available quantity: ${Number(available).toLocaleString("en-IN")}`,
      );
      setIsModalOpen(false);
      setConfirmModal({ isOpen: false, payload: null });
      setIdempotencyKey("");
      setPage(1);
      setTypeFilter("all");
      setDirectionFilter("all");
      setSearchInput("");
      setSearch("");
      setRefreshVersion((version) => version + 1);
      void loadBatches();
    } catch (err: any) {
      const apiErrors: string[] = err.response?.data?.errors || [];
      const message = err.response?.data?.message;
      showToast.error(
        apiErrors.length > 0
          ? apiErrors.join(" ")
          : message || "Failed to create stock adjustment",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const normalizedBatchSearch = batchSearch.trim().toLowerCase();
  const batchMatches =
    normalizedBatchSearch.length >= 2
      ? batches.filter((batch) =>
          [
            batch.batchNumber,
            batch.product?.name || "",
            batch.product?.sku || "",
          ].some((value) =>
            value.toLowerCase().includes(normalizedBatchSearch),
          ),
        )
      : [];
  const selectedBatch = batches.find((batch) => batch.id === formData.batchId);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Stock Adjustments
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Record stock changes for damage, expiry, corrections, transfers and
            write-offs
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => openModal("adjustment")}
            disabled={!selectedStoreId}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            New Adjustment
          </button>
          <button
            onClick={() => openModal("writeoff")}
            disabled={!selectedStoreId}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            Write-off
          </button>
        </div>
      </div>

      {/* Quick Guide */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-sm font-semibold text-blue-800">Adjustment</div>
          <div className="text-xs text-blue-600 mt-1">
            Correction, Transfer In/Out, Opening Stock
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-sm font-semibold text-red-800">Write-off</div>
          <div className="text-xs text-red-600 mt-1">
            Damaged or expired stock removal
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="text-sm font-semibold text-green-800">Stock IN</div>
          <div className="text-xs text-green-600 mt-1">
            Opening stock, transfers received, corrections up
          </div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
          <div className="text-sm font-semibold text-orange-800">Stock OUT</div>
          <div className="text-xs text-orange-600 mt-1">
            Damage, expiry, theft, returns, corrections down
          </div>
        </div>
      </div>

      {!selectedStoreId && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
          Select a store from the header to manage stock adjustments.
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <form
          className="flex flex-wrap items-end gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setSearch(searchInput.trim());
          }}
        >
          <label className="min-w-[260px] flex-1 text-xs font-medium uppercase tracking-wide text-gray-600">
            Search history
            <input
              type="search"
              aria-label="Search adjustment history"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Product, SKU, batch, reason or reference"
              className="mt-1 w-full rounded-lg border px-4 py-2 text-sm font-normal normal-case tracking-normal focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
          <label className="text-xs font-medium uppercase tracking-wide text-gray-600">
            Type
            <select
              aria-label="Filter adjustment type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="mt-1 block px-4 py-2 border rounded-lg text-sm font-normal normal-case focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              {TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {type.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium uppercase tracking-wide text-gray-600">
            Direction
            <select
              aria-label="Filter adjustment direction"
              value={directionFilter}
              onChange={(event) => {
                setDirectionFilter(event.target.value);
                setPage(1);
              }}
              className="mt-1 block px-4 py-2 border rounded-lg text-sm font-normal normal-case focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All directions</option>
              <option value="IN">Stock IN</option>
              <option value="OUT">Stock OUT</option>
            </select>
          </label>
          <button
            type="submit"
            className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Search
          </button>
          {(search || typeFilter !== "all" || directionFilter !== "all") && (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                setSearch("");
                setTypeFilter("all");
                setDirectionFilter("all");
                setPage(1);
              }}
              className="rounded-lg border px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear filters
            </button>
          )}
          <span className="ml-auto pb-2 text-sm text-gray-500">
            {total} adjustment{total === 1 ? "" : "s"}
          </span>
        </form>
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="text-gray-500">Loading adjustments...</div>
        </div>
      )}

      {!loading && adjustments.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="text-gray-500 mb-4">
            {search || typeFilter !== "all" || directionFilter !== "all"
              ? "No adjustments match these filters"
              : "No adjustments have been recorded"}
          </div>
        </div>
      )}

      {!loading && adjustments.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Batch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Qty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {adjustments.map((adj) => (
                  <tr key={adj.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {adj.batch?.product?.name || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {adj.batch?.batchNumber || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${TYPE_INFO[adj.type]?.color || "bg-gray-100 text-gray-800"}`}
                      >
                        {TYPE_INFO[adj.type]?.label ||
                          adj.type.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div
                        className={`text-sm font-semibold ${adj.direction === "IN" ? "text-green-600" : "text-red-600"}`}
                      >
                        {adj.direction === "IN" ? "+" : "-"}
                        {adj.quantity}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {adj.reason || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {adj.reference || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {new Date(adj.createdAt).toLocaleString("en-IN")}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)}{" "}
                of {total} · Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsModalOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="stock-adjustment-dialog-title"
            className="relative z-10 flex max-h-[calc(100dvh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-h-[calc(100dvh-3rem)]"
          >
            <div className="shrink-0 border-b px-6 py-5">
              <h2
                id="stock-adjustment-dialog-title"
                className="text-xl font-semibold mb-2"
              >
                {mode === "writeoff" ? "Write-off Stock" : "New Adjustment"}
              </h2>
              <p className="text-sm text-gray-500">
                {mode === "writeoff"
                  ? "Remove damaged or expired stock from inventory. This reduces stock."
                  : "Record a stock change — correction, transfer, opening stock, or other adjustment."}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Batch <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="search"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={
                      normalizedBatchSearch.length >= 2 && !selectedBatch
                    }
                    aria-controls="adjustment-batch-results"
                    aria-label="Find and select batch"
                    value={batchSearch}
                    onChange={(event) => {
                      setBatchSearch(event.target.value);
                      if (formData.batchId) {
                        setFormData((previous) => ({
                          ...previous,
                          batchId: "",
                        }));
                      }
                    }}
                    placeholder="Type product, SKU or batch number"
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {selectedBatch ? (
                    <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {selectedBatch.product?.name || "Unnamed product"}
                        </p>
                        <p className="text-xs text-gray-600">
                          Batch {selectedBatch.batchNumber}
                          {selectedBatch.product?.sku
                            ? ` · SKU ${selectedBatch.product.sku}`
                            : ""}
                          {` · ${selectedBatch.quantity} available`}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((previous) => ({
                            ...previous,
                            batchId: "",
                          }));
                          setBatchSearch("");
                        }}
                        className="shrink-0 text-sm font-medium text-blue-700 hover:text-blue-900"
                      >
                        Change
                      </button>
                    </div>
                  ) : normalizedBatchSearch.length < 2 ? (
                    <p className="mt-1 text-xs text-gray-500">
                      Type at least 2 characters to search {batches.length}{" "}
                      in-stock batches.
                    </p>
                  ) : (
                    <div
                      id="adjustment-batch-results"
                      role="listbox"
                      aria-label="Matching batches"
                      className="mt-2 max-h-52 overflow-y-auto rounded-lg border bg-white shadow-sm"
                    >
                      {batchMatches.length === 0 ? (
                        <p className="px-3 py-3 text-sm text-gray-500">
                          No matching in-stock batches
                        </p>
                      ) : (
                        batchMatches.slice(0, 50).map((batch) => (
                          <button
                            key={batch.id}
                            type="button"
                            role="option"
                            aria-selected={false}
                            aria-label={`Select batch ${batch.batchNumber} for ${batch.product?.name || "product"}`}
                            onClick={() => {
                              setFormData((previous) => ({
                                ...previous,
                                batchId: batch.id,
                              }));
                              setBatchSearch("");
                            }}
                            className="block w-full border-b px-3 py-2 text-left last:border-b-0 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                          >
                            <span className="block text-sm font-medium text-gray-900">
                              {batch.product?.name || "Unnamed product"}
                            </span>
                            <span className="block text-xs text-gray-600">
                              Batch {batch.batchNumber}
                              {batch.product?.sku
                                ? ` · SKU ${batch.product.sku}`
                                : ""}
                              {` · ${batch.quantity} available`}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    aria-label="Adjustment type"
                    value={formData.type}
                    onChange={(e) => {
                      const type = e.target.value as AdjustmentType;
                      const info = TYPE_INFO[type];
                      setFormData((prev) => ({
                        ...prev,
                        type,
                        direction:
                          info?.direction === "BOTH"
                            ? prev.direction
                            : (info?.direction as any) || "OUT",
                      }));
                    }}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {(mode === "writeoff" ? WRITE_OFF_TYPES : TYPE_OPTIONS).map(
                      (type) => (
                        <option key={type} value={type}>
                          {TYPE_INFO[type]?.label || type.replace(/_/g, " ")}
                        </option>
                      ),
                    )}
                  </select>
                  {TYPE_INFO[formData.type] && (
                    <p className="text-xs text-gray-500 mt-1">
                      {TYPE_INFO[formData.type].description}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    aria-label="Adjustment quantity"
                    type="number"
                    min={1}
                    step={1}
                    value={formData.quantity}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        quantity: Number(e.target.value),
                      }))
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {mode === "adjustment" &&
                  TYPE_INFO[formData.type]?.direction === "BOTH" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Direction
                      </label>
                      <select
                        aria-label="Adjustment direction"
                        value={formData.direction || "OUT"}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            direction: e.target.value as "IN" | "OUT",
                          }))
                        }
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="OUT">Reduce Stock (found less)</option>
                        <option value="IN">Increase Stock (found more)</option>
                      </select>
                    </div>
                  )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <input
                    aria-label="Adjustment reason"
                    type="text"
                    value={formData.reason || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    placeholder="e.g. 3 bottles damaged during handling"
                    maxLength={250}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference
                  </label>
                  <input
                    aria-label="Adjustment reference"
                    type="text"
                    value={formData.reference || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        reference: e.target.value,
                      }))
                    }
                    placeholder="Transfer number, incident ID or document reference"
                    maxLength={100}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    aria-label="Adjustment notes"
                    value={formData.notes || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    rows={3}
                    maxLength={1000}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-3 border-t bg-white px-6 py-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, payload: null })}
        onConfirm={handleConfirm}
        title="Confirm Stock Adjustment"
        message={
          confirmModal.payload
            ? `${confirmModal.payload.direction === "IN" ? "Add" : "Remove"} ${confirmModal.payload.quantity} units ${confirmModal.payload.direction === "IN" ? "to" : "from"} ${selectedBatch?.product?.name || "the selected product"}, batch ${selectedBatch?.batchNumber || "-"}. Reason: ${confirmModal.payload.reason}`
            : "Confirm this stock adjustment."
        }
        confirmText={isSubmitting ? "Processing..." : "Confirm"}
        isLoading={isSubmitting}
        variant="warning"
      />
    </div>
  );
}
